import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useWorkspace } from "../../store/WorkspaceContext";
import { DEFAULT_CONNECTION } from "../../lib/transport";
import { plotterConnected } from "../../engine/detect";

const KEY = "xpattern.tour";
const EVT = "xpattern:tour";
export const startTour = () => { localStorage.setItem(KEY, "pill"); window.dispatchEvent(new Event(EVT)); };

// Step → [target testid, spotlight?, title, text]
const STEPS = {
  pill: ["machine-status-pill", "Step 1 · Connect your plotter", "Click here to open Plotter settings — we'll detect your plotter automatically."],
  usb: [null, "Plug in your plotter", "Please connect your plotter's USB cable to this computer and switch the plotter on."],
  detect: ["plotter-list", "Choose your plotter", "Your plotter appears in this list as soon as it's plugged in. Click it, then press Select — the guide continues once it is connected."],
  connected: [null, "Connected!", "Your plotter is ready — every cut goes straight to it, true to size."],
  dashboard: ["account-menu-btn", "Step 2 · Your dashboard", "Click here to open My Dashboard."],
  dashboardMsg: [null, "Welcome to Your Dashboard", ["Plan & billing", "Connected plotters", "Vehicle pattern requests", "Feedback & reviews", "Account settings"], "Your hub for everything outside the cutting table. Close it when you're ready."],
  service: ["service-select", "Step 3 · Pick your service", "Choose the film service you're cutting today."],
  year: ["year-select", "Step 4 · Vehicle year", "Select the model year."],
  make: ["make-select", "Step 5 · Make", "Select the manufacturer."],
  model: ["model-select", "Step 6 · Model", "Select the model — its glass patterns load onto your roll."],
  done: [null, "You're all set", "Add the glass you need to the roll and press Cut. Enjoy Xpattern!"],
};

const useRect = (testId, step) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!testId) { setRect(null); return undefined; }
    const tick = () => { const el = document.querySelector(`[data-testid="${testId}"]`); setRect(el ? el.getBoundingClientRect() : null); };
    tick(); const t = setInterval(tick, 250); window.addEventListener("resize", tick);
    return () => { clearInterval(t); window.removeEventListener("resize", tick); };
  }, [testId, step]);
  return rect;
};

const pickerValue = (id) => document.querySelector(`[data-testid="${id}"]`)?.getAttribute("data-value") || "";

export const GuidedTour = () => {
  const { state } = useWorkspace();
  const [step, setStep] = useState(() => localStorage.getItem(KEY) || "");
  useEffect(() => { const on = () => setStep(localStorage.getItem(KEY) || ""); window.addEventListener(EVT, on); return () => window.removeEventListener(EVT, on); }, []);
  const go = (s) => { if (s) localStorage.setItem(KEY, s); else localStorage.removeItem(KEY); setStep(s); };

  const conn = { ...DEFAULT_CONNECTION, ...state.connection };
  const connected = plotterConnected(conn, state.bridge);

  // advance on workspace state
  useEffect(() => {
    if (step === "pill" && state.dialog === "settings") go("usb");
    if (step === "detect" && connected) { go("connected"); setTimeout(() => go("dashboard"), 2600); }
    if (step === "detect" && !connected && state.dialog !== "settings") go("pill"); // closed the dialog without connecting → back to step 1
    if (step === "usb" && state.dialog !== "settings") go("pill");
    if (step === "dashboard" && state.dialog === "profile") go("dashboardMsg");
    if (step === "dashboardWait" && state.dialog !== "profile") go("service");
  }, [step, state.dialog, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // advance on vehicle pickers
  useEffect(() => {
    if (!["service", "year", "make", "model"].includes(step)) return undefined;
    const t = setInterval(() => {
      if (step === "service" && pickerValue("service-select")) go("year");
      else if (step === "year" && pickerValue("year-select")) go("make");
      else if (step === "make" && pickerValue("make-select")) go("model");
      else if (step === "model" && pickerValue("model-select")) { go("done"); setTimeout(() => go(""), 3500); }
    }, 300);
    return () => clearInterval(t);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const def = STEPS[step];
  const rect = useRect(def?.[0], step);
  if (!def) return null;
  const [, title, text, footer] = def;
  const modal = ["usb", "dashboardMsg"].includes(step);
  const dim = !["service", "year", "make", "model", "detect"].includes(step); // keep drop-downs and the plotter list fully readable
  const spotlight = def[0] && rect;
  const sideways = spotlight && rect.left < window.innerWidth / 2 && rect.right + 370 < window.innerWidth; // sidebar targets: card to the right so drop-downs stay clear
  const cardStyle = spotlight
    ? sideways ? { top: Math.max(12, Math.min(rect.top - 8, window.innerHeight - 190)), left: rect.right + 16 }
      : { top: Math.min(rect.bottom + 14, window.innerHeight - 190), left: Math.max(12, Math.min(rect.left + rect.width / 2 - 170, window.innerWidth - 352)) }
    : modal ? { top: "50%", left: "50%", transform: "translate(-50%,-50%)" } : { bottom: 24, left: "50%", transform: "translateX(-50%)" };
  const stop = (e) => e.stopPropagation();
  return (
    <div className="pointer-events-none fixed inset-0 z-[200]" data-testid="guided-tour" data-step={step}>
      {spotlight && <div className={`absolute rounded-lg ring-2 ring-[#4c9ffe] ${dim ? "tour-pulse" : "tour-pulse-light"}`} style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: dim ? "0 0 0 9999px rgba(3,6,12,0.62), 0 0 24px 4px rgba(76,159,254,0.55)" : "0 0 24px 4px rgba(76,159,254,0.55)" }} />}
      {modal && <div className="absolute inset-0 bg-[#03060c]/60" />}
      <div onPointerDown={stop} onClick={stop} className="glass-panel absolute w-[340px] p-4 shadow-2xl" style={{ ...cardStyle, pointerEvents: "auto" }} data-testid={`tour-card-${step}`}>
        <button data-testid="tour-skip" title="Skip the guide" onClick={() => go("")} className="absolute right-2.5 top-2.5 text-[#6b7079] hover:text-white"><X className="h-4 w-4" /></button>
        <div className="px-3 text-center">
          <div className={step === "dashboardMsg" ? "text-[18px] font-bold tracking-tight text-[#f5c542]" : `text-[14px] font-semibold ${step === "connected" ? "text-[#3ecf8e]" : "text-white"}`} data-testid="tour-title">{title}</div>
          {step === "dashboardMsg" && <div className="mx-auto mt-1.5 h-[2px] w-12 rounded-full bg-[#f5c542]/70" />}
          {footer && <div className="mt-2 text-[12px] leading-relaxed text-[#9aa0a8]">{footer}</div>}
          {Array.isArray(text)
            ? <ul className="mx-auto mt-2 inline-block text-left text-[12.5px] leading-relaxed text-[#c9ccd2]" data-testid="tour-bullets">{text.map((t) => <li key={t} className="flex items-start gap-2"><span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-[#4c9ffe]" />{t}</li>)}</ul>
            : <div className="mt-1 text-[12.5px] leading-relaxed text-[#c9ccd2]">{text}</div>}
          {step === "detect" && <div className="mt-2 flex items-center justify-center gap-2 text-[11.5px] text-[#f5a524]"><span className="h-2 w-2 animate-pulse rounded-full bg-[#f5a524]" /> Waiting for the plotter…</div>}
        </div>
        {step === "usb" && <button data-testid="tour-usb-ok" onClick={() => go("detect")} className="glass-cta mt-3 w-full py-2 text-[13px] font-semibold" style={{ "--glass-accent": "#2f80ff" }}>OK, it's plugged in</button>}
        {step === "dashboardMsg" && <button data-testid="tour-dashboard-ok" onClick={() => go("dashboardWait")} className="glass-cta mt-3 w-full py-2 text-[13px] font-semibold" style={{ "--glass-accent": "#2f80ff" }}>Got it</button>}
      </div>
    </div>
  );
};
